/**
 * @fileoverview InteractAdapter - interact.js の薄いラッパー
 * ユーザー入力ハンドラーへの橋渡しを担当
 */

import interact from 'interactjs';

import { PIECE_SIZE } from '../../core/constants.js';
import gameState from '../../core/game-state.js';
import { getGameBoard } from '../../gameplay/game-controls.js';
import { STATE_PATHS } from '../../shared/constants/state-paths.js';
import { MinimalErrorTracker } from '../../shared/log.js';
import { canInteract } from '../../shared/utils/game-state-utils.js';
import { DRAG_CONSTANTS, DOM_CONSTANTS } from '../constants.js';
import { addPointerListeners } from '../handlers/pointer-input.js';
import { getSwipeTargetUI } from '../handlers/user-input-helpers.js';

import {
  beginDragSession,
  getDragState,
  markSwapExecuted,
  resetDragState,
  setDragAxis,
  setDragOffset,
} from './drag-state.js';
import { DragSystem } from './piece-interaction.js';

// CSS 定数
const CSS_CLASS_PIECE_SHADOW = 'piece-shadow';
const TRANSFORM_ZERO = 'translate(0px, 0px)';
const DRAG_DATA_ATTRS = ['data-x', 'data-y', 'data-start-x', 'data-start-y'];
const clamp = (value, limit) => Math.max(Math.min(value, limit), -limit);
const isDrawerOpen = () =>
  document.documentElement.classList.contains(DOM_CONSTANTS.DRAWER_OPEN_CLASS);

//-------------
// InteractAdapter クラス
//-------------
export class InteractAdapter {
  constructor() {
    this.instance = null;
  }

  /**
   * 初期化
   */
  initialize() {
    this.instance = interact(`.${CSS_CLASS_PIECE_SHADOW}`)
      .draggable({
        listeners: {
          start: this.onStart.bind(this),
          move: this.onMove.bind(this),
          end: this.onEnd.bind(this),
        },
        allowFrom: `.${CSS_CLASS_PIECE_SHADOW}`,
        ignoreFrom: '',
        enabled: true,
      })
      .on('dragleave', this.onDragLeave.bind(this));

    // GameState 変化に合わせて有効/無効を切り替え
    gameState.subscribe((event) => {
      if (event.path && event.path.startsWith('core.')) {
        this.updateEnabled();
      }
    });

    this.addSwipeEvents();
    return this;
  }

  /**
   * 有効/無効更新
   */
  updateEnabled() {
    if (!this.instance) return;

    try {
      const canInteractFlag = canInteract(gameState);
      this.instance.draggable({ enabled: canInteractFlag });
    } catch (error) {
      MinimalErrorTracker.warn('INTERACT_ADAPTER', 'Enable control failed', error);
      this.instance.draggable({ enabled: false });
    }
  }

  /**
   * ドラッグ開始
   */
  onStart(event) {
    const ui = gameState.getState(STATE_PATHS.UI);
    if (ui?.isSkillTargeting) return;
    if (!canInteract(gameState)) return;
    if (DragSystem.hasInputCooldown()) return;

    if (event.pointerType && !event.isPrimary) return;
    if (!event.target?.getBoundingClientRect) return;

    try {
      beginDragSession(event.clientX, event.clientY);
    } catch (error) {
      MinimalErrorTracker.warn('INTERACT_ADAPTER', 'Drag start failed', error);
    }
  }

  /**
   * ドラッグ移動
   */
  onMove(event) {
    const ui = gameState.getState(STATE_PATHS.UI);
    if (ui?.isSkillTargeting) return;
    if (!canInteract(gameState)) return;
    if (!event.target?.getBoundingClientRect) return;

    const drag = getDragState();
    if (!drag.isDragging) return;

    const { axis, dx, dy } = calculateDragMovement(drag, event.dx, event.dy);

    setDragOffset(dx, dy);
    updateDragAxisIfNeeded(axis, drag.axis);

    if (!applyDragTransformSafely(event.target, dx, dy)) {
      this.endDrag();
      return;
    }

    if (!drag.swapExecuted && shouldExecuteSwap(dx, dy)) {
      markSwapExecuted();
      this.executeImmediateSwap(event);
    }
  }

  /**
   * ドラッグ終了
   */
  onEnd(event) {
    const ui = gameState.getState(STATE_PATHS.UI);
    if (ui?.isSkillTargeting) return;
    if (!canInteract(gameState)) return;

    const drag = getDragState();
    if (!drag.isDragging) return;

    if (drag.swapExecuted) {
      this.endDrag();
      return;
    }

    event.target.style.transform = TRANSFORM_ZERO;
    DRAG_DATA_ATTRS.forEach((attr) => event.target.removeAttribute(attr));
    this.endDrag();
  }

  /**
   * ドラッグ離脱
   */
  onDragLeave(event) {
    const drag = getDragState();
    if (!drag.isDragging) return;

    event.target.style.transform = TRANSFORM_ZERO;
    DRAG_DATA_ATTRS.forEach((attr) => event.target.removeAttribute(attr));
    this.endDrag();
  }

  /**
   * スワップ実行
   */
  executeImmediateSwap(event) {
    const drag = getDragState();
    if (!drag.isDragging) return;

    DragSystem.handleDrop({
      target: event.target,
      clientX: drag.startX + drag.currentX,
      clientY: drag.startY + drag.currentY,
    });
  }

  /**
   * 共有ドラッグ状態のリセット
   */
  endDrag() {
    resetDragState();
  }

  /**
   * スワイプイベント追加
   */
  addSwipeEvents() {
    const boardElement = document.getElementById('board');
    if (!boardElement) {
      MinimalErrorTracker.warn('INTERACT_ADAPTER', 'Board element not found for swipe setup');
      return;
    }

    let swipeState = { startX: 0, startY: 0, pointerId: null, isActive: false };
    const pointerHandlers = this.#createSwipeHandlers(swipeState);

    const removePointerListeners = addPointerListeners(boardElement, pointerHandlers, {
      capture: true,
      preventDefault: true,
      shouldProcess: () => !isDrawerOpen(),
      getDedupeKey: (event) =>
        `${Math.round(event.clientX)}:${Math.round(event.clientY)}:${event.pointerType}`,
    });

    this.removeSwipeListeners = () => {
      removePointerListeners?.();
      swipeState = null;
    };
  }

  #createSwipeHandlers(swipeState) {
    const resetSwipeState = () => {
      swipeState.isActive = false;
      swipeState.pointerId = null;
      swipeState.startX = 0;
      swipeState.startY = 0;
    };

    return {
      onDown: (event) => {
        if (!this.#canAcceptSwipeStart()) {
          resetSwipeState();
          return false; // 明示的に拒否してpointer-input側でactivePointerIdをクリア
        }
        swipeState.startX = event.clientX;
        swipeState.startY = event.clientY;
        swipeState.pointerId = event.pointerId;
        swipeState.isActive = true;
        return true; // 明示的に受理済みを示す
      },
      onMove: (event) => {
        event.preventDefault();
      },
      onUp: (event) => {
        if (!swipeState.isActive || swipeState.pointerId !== event.pointerId) {
          return;
        }
        if (!this.#canAcceptSwipeEnd()) {
          resetSwipeState();
          return;
        }
        this.#processSwipe(event, swipeState);
        resetSwipeState();
      },
      onCancel: (event) => {
        if (!swipeState.isActive || (event && swipeState.pointerId !== event.pointerId)) {
          return;
        }
        resetSwipeState();
        DragSystem.setInvalidSwapTime();
      },
    };
  }

  #canAcceptSwipeStart() {
    const ui = gameState.getState(STATE_PATHS.UI);
    if (ui?.isSkillTargeting) return false;
    if (!canInteract(gameState)) return false;
    if (DragSystem.hasInputCooldown()) return false;
    return true;
  }

  #canAcceptSwipeEnd() {
    const ui = gameState.getState(STATE_PATHS.UI);
    if (ui?.isSkillTargeting) return false;
    if (!canInteract(gameState)) return false;
    return true;
  }

  #processSwipe(event, swipeState) {
    const diffX = event.clientX - swipeState.startX;
    const diffY = event.clientY - swipeState.startY;
    const confirmThreshold = PIECE_SIZE * DRAG_CONSTANTS.CONFIRM_RATIO;

    if (Math.abs(diffX) < confirmThreshold && Math.abs(diffY) < confirmThreshold) {
      DragSystem.setInvalidSwapTime();
      return;
    }

    const swipeResult = getSwipeTargetUI(swipeState.startX, swipeState.startY, diffX, diffY);
    if (!swipeResult) {
      DragSystem.setInvalidSwapTime();
      return;
    }

    this.#executeSwipeSwap(swipeResult);
  }

  #executeSwipeSwap({ row, col, newRow, newCol, target }) {
    const gameBoard = getGameBoard();
    const piece = gameBoard.getPiece(row, col);
    if (!piece) {
      DragSystem.setInvalidSwapTime();
      return;
    }

    const newPiece = gameBoard.getPiece(newRow, newCol);
    if (newPiece) {
      DragSystem.executeSwap(piece, newPiece);
    }

    target.style.transform = TRANSFORM_ZERO;
    DRAG_DATA_ATTRS.forEach((attr) => target.removeAttribute(attr));
  }

  /**
   * クリーンアップ
   */
  destroy() {
    if (this.instance) {
      this.instance.unset();
      this.instance = null;
    }

    if (typeof this.removeSwipeListeners === 'function') {
      this.removeSwipeListeners();
      this.removeSwipeListeners = null;
    }
  }
}

function calculateDragMovement(drag, deltaX, deltaY) {
  const nextX = drag.currentX + deltaX;
  const nextY = drag.currentY + deltaY;
  const axis = resolveDragAxis(drag.axis, nextX, nextY);
  const { dx, dy } = limitDragOffset(axis, nextX, nextY, PIECE_SIZE);
  return { axis, dx, dy };
}

function resolveDragAxis(currentAxis, nextX, nextY) {
  if (currentAxis) return currentAxis;
  if (Math.abs(nextX) > Math.abs(nextY)) return 'x';
  if (Math.abs(nextY) > Math.abs(nextX)) return 'y';
  return null;
}

function limitDragOffset(axis, nextX, nextY, maxDistance) {
  return {
    dx: clamp(axis === 'y' ? 0 : nextX, maxDistance),
    dy: clamp(axis === 'x' ? 0 : nextY, maxDistance),
  };
}

function updateDragAxisIfNeeded(nextAxis, previousAxis) {
  if (nextAxis && nextAxis !== previousAxis) {
    setDragAxis(nextAxis);
  }
}

function applyDragTransformSafely(target, dx, dy) {
  try {
    const wrapper = getDragWrapper(target);
    if (!wrapper) throw new Error('Missing drag wrapper');
    wrapper.style.transform = `translate(${dx}px, ${dy}px)`;
    wrapper.dataset.x = dx;
    wrapper.dataset.y = dy;
    return true;
  } catch (error) {
    MinimalErrorTracker.warn('INTERACT_ADAPTER', 'Drag transform failed', error);
    return false;
  }
}

function getDragWrapper(target) {
  if (!target) return null;
  if (typeof target.closest === 'function') {
    const shadow = target.closest(`.${CSS_CLASS_PIECE_SHADOW}`);
    if (shadow) return shadow;
  }
  return target;
}

function shouldExecuteSwap(dx, dy) {
  const threshold = PIECE_SIZE / 4;
  return Math.abs(dx) >= threshold || Math.abs(dy) >= threshold;
}

//-------------
// シングルトンインスタンス
//-------------
export const interactAdapter = new InteractAdapter();

//-------------
// 公開 API 取得関数
//-------------
export function addDraggableEvents() {
  return interactAdapter.initialize();
}
